/**
 * Collection Intro Section
 * Read More/Less with truncation detection
 * Cross-browser compatible
 */

(function() {
  'use strict';

  class CollectionIntro {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId || '';
      this.collectionHandle = container.dataset.collectionHandle || '';
      this.description = container.querySelector('[data-description]');
      this.readMoreBtn = container.querySelector('[data-read-more]');
      this.textSpan = this.readMoreBtn?.querySelector('[data-text]');
      this.isExpanded = false;
      this.expandedHeight = 0; // Store the full height when expanded
      
      this.collapsedText = this.readMoreBtn?.dataset.collapsedText || 'Read More';
      this.expandedText = this.readMoreBtn?.dataset.expandedText || 'Read Less';
      
      this.init();
    }
    
    init() {
      if (!this.description) return;
      
      // Initial check first (before calculating expanded height)
      this.checkTruncation();
      
      // Calculate expanded height after a short delay to ensure CSS is loaded
      setTimeout(() => {
        this.calculateExpandedHeight();
      }, 100);
      
      // Bind events
      this.bindEvents();
      
      // Debounced resize handler
      this.handleResize = this.debounce(() => {
        this.calculateExpandedHeight();
        this.checkTruncation();
      }, 250);
      window.addEventListener('resize', this.handleResize);
      
      // Handle orientation change on mobile
      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          this.calculateExpandedHeight();
          this.checkTruncation();
        }, 100);
      });
    }
    
    calculateExpandedHeight() {
      if (!this.description) return;
      
      // Temporarily remove truncation and inline styles to measure full height
      const wasTruncated = this.description.classList.contains('is-truncated');
      const hadInlineMaxHeight = this.description.style.maxHeight;
      
      this.description.classList.remove('is-truncated');
      this.description.style.maxHeight = '';
      
      // Force reflow to ensure CSS is applied
      void this.description.offsetHeight;
      
      // Get the actual scroll height
      const scrollHeight = this.description.scrollHeight;
      
      // Cap the max height to prevent extremely large values (safety limit: 5000px)
      // This prevents issues when CSS isn't fully loaded yet
      this.expandedHeight = Math.min(scrollHeight, 5000);
      
      // Restore previous state
      if (wasTruncated) {
        this.description.classList.add('is-truncated');
      }
      if (hadInlineMaxHeight) {
        this.description.style.maxHeight = hadInlineMaxHeight;
      }
    }
    
    checkTruncation() {
      if (!this.description || !this.readMoreBtn) return;
      
      // Store current state
      const wasExpanded = this.isExpanded;
      
      // Remove any inline max-height that might interfere
      const hadInlineMaxHeight = this.description.style.maxHeight;
      if (hadInlineMaxHeight && !wasExpanded) {
        this.description.style.maxHeight = '';
      }
      
      // Temporarily apply truncation to measure
      if (wasExpanded) {
        this.description.classList.add('is-truncated');
      }
      
      // Force reflow to get accurate measurements
      void this.description.offsetHeight;
      
      // Check if text overflows (with 1px tolerance for rounding)
      const isTruncated = this.description.scrollHeight > this.description.clientHeight + 1;
      // CLS: from here the measured result decides, not the server-side length hint
      this.readMoreBtn.classList.add('is-checked');
      
      if (isTruncated) {
        this.readMoreBtn.classList.add('is-visible');
        
        // Restore expanded state if it was expanded
        if (wasExpanded) {
          this.description.classList.remove('is-truncated');
        }
      } else {
        this.readMoreBtn.classList.remove('is-visible');
        this.description.classList.remove('is-truncated');
        // Remove inline max-height if content doesn't need truncation
        this.description.style.maxHeight = '';
        this.isExpanded = false;
        this.updateButtonState();
      }
    }
    
    toggle() {
      this.isExpanded = !this.isExpanded;
      
      // Recalculate height if needed (in case content changed)
      this.calculateExpandedHeight();
      
      // Get computed styles for calculations
      const computedStyle = getComputedStyle(this.description);
      const expandDuration = parseInt(computedStyle.getPropertyValue('--expand-duration')) || 300;
      const isDesktop = window.innerWidth >= 990;
      
      // Update the max-height for smooth animation
      if (this.isExpanded) {
        // Expanding: get current collapsed height first
        const collapsedHeight = this.description.clientHeight;
        // Set max-height to current height (for smooth transition start)
        this.description.style.maxHeight = collapsedHeight + 'px';
        // Remove truncated class to allow full display
        this.description.classList.remove('is-truncated');
        // Force reflow
        void this.description.offsetHeight;
        // Recalculate expanded height to ensure it's accurate
        this.calculateExpandedHeight();
        // Now transition to full expanded height (cap at 5000px for safety)
        const safeExpandedHeight = Math.min(this.expandedHeight || 2000, 5000);
        this.description.style.maxHeight = safeExpandedHeight + 'px';
      } else {
        // Collapsing: get current expanded height
        const currentHeight = this.description.scrollHeight;
        
        // Calculate collapsed height using CSS variables
        const lineClamp = isDesktop 
          ? parseInt(computedStyle.getPropertyValue('--line-clamp-desktop')) || 2
          : parseInt(computedStyle.getPropertyValue('--line-clamp-mobile')) || 2;
        const lineHeight = isDesktop
          ? parseFloat(computedStyle.getPropertyValue('--description-line-height-desktop')) || 1.55
          : parseFloat(computedStyle.getPropertyValue('--description-line-height-mobile')) || 1.55;
        const fontSize = parseFloat(computedStyle.fontSize) || (isDesktop ? 16 : 15);
        // Exact calculation without extra padding to prevent partial line visibility
        const collapsedHeight = lineClamp * lineHeight * fontSize;
        
        // Set max-height to current height first (establish starting point)
        this.description.style.maxHeight = currentHeight + 'px';
        // Ensure truncated class is removed so transition works
        this.description.classList.remove('is-truncated');
        // Force reflow
        void this.description.offsetHeight;
        // Now transition to collapsed height
        this.description.style.maxHeight = collapsedHeight + 'px';
        // After animation completes, add truncated class and remove inline style
        setTimeout(() => {
          if (!this.isExpanded) {
            this.description.classList.add('is-truncated');
            this.description.style.maxHeight = '';
          }
        }, expandDuration);
      }
      
      this.updateButtonState();
      
      // Announce change to screen readers
      this.readMoreBtn.setAttribute('aria-expanded', this.isExpanded.toString());
      
      if (window.jrsTrackEvent) {
        window.jrsTrackEvent('intro_read_more_toggle', {
          action: this.isExpanded ? 'expand' : 'collapse',
          section_id: this.sectionId,
          collection_handle: this.collectionHandle,
          has_description: !!this.description
        }, { prefix: '' });
      }
    }
    
    updateButtonState() {
      if (!this.readMoreBtn || !this.textSpan) return;
      
      this.readMoreBtn.setAttribute('aria-expanded', this.isExpanded.toString());
      this.textSpan.textContent = this.isExpanded ? this.expandedText : this.collapsedText;
    }
    
    bindEvents() {
      if (this.readMoreBtn) {
        // Click event
        this.readMoreBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle();
        });
        
        // Keyboard support (Enter and Space)
        this.readMoreBtn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggle();
          }
        });
      }
    }
    
    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }
    
    destroy() {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  // Initialize
  function initCollectionIntro() {
    document.querySelectorAll('[data-section-type="collection-intro"]').forEach(container => {
      if (!container.classList.contains('js-initialized')) {
        container.classList.add('js-initialized');
        new CollectionIntro(container);
      }
    });
  }

  // DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionIntro);
  } else {
    initCollectionIntro();
  }

  // Shopify Theme Editor Support
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('[data-section-type="collection-intro"]')) {
      initCollectionIntro();
    }
  });

  // Re-initialize on section reorder (theme editor)
  document.addEventListener('shopify:section:reorder', initCollectionIntro);
})();
